import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBaseQuery } from '../useBaseQuery';
import { useBaseMutation } from '../useBaseMutation';
import { changePassword, createUser, getAllUsers, updateUser } from '@/shared/api/client/nub-user-resource';
import type { NubUserDTO, PasswordChangeRequest } from '@/shared/api/model';

type CustomerQueryDto = {
    branch?: string;
    searchKey?: string;
    limit?: number;
    userId?: string;
};

export const useUsers = (branch: string) => {
    const [searchParams, setSearchParams] = useState<CustomerQueryDto>({
        branch,
        searchKey: '',
        limit: 30,
        userId: '',
    });

    const queryClient = useQueryClient();

    const [{ data: users, isLoading, error }, refetchUsers] = useBaseQuery<NubUserDTO[], Error>(
        ['users', searchParams],
        async () => {
            const allUsers = await getAllUsers();
            const normalizedSearch = (searchParams.searchKey || '').trim().toLowerCase();
            const normalizedUserId = (searchParams.userId || '').trim().toLowerCase();

            return (allUsers || [])
                .filter((user) => !branch || !user.branch || user.branch === branch)
                .filter((user) => !normalizedUserId || (user.userId || '').toLowerCase().includes(normalizedUserId))
                .filter((user) => {
                    if (!normalizedSearch) {
                        return true;
                    }
                    return [user.fullName, user.email, user.userId]
                        .filter(Boolean)
                        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
                })
                .slice(0, searchParams.limit || 30);
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            placeholderData: (previousData: NubUserDTO[] | undefined) => previousData,
        }
    );

    const saveUserMutation = useBaseMutation<NubUserDTO, NubUserDTO>(
        (user: NubUserDTO) => user.userId ? updateUser(user.userId, user) : createUser(user),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] })
                    .then(() => {console.log('User saved successfully')});
            },
        }
    );

    const changePasswordMutation = useBaseMutation<NubUserDTO, { userId: string; data: PasswordChangeRequest }>(
        ({ userId, data }) => changePassword(userId, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] })
                    .then(() => {console.log('Password changed successfully')});
            },
        }
    );

    const searchUsers = (params: Partial<CustomerQueryDto>) => {
        setSearchParams((prev: CustomerQueryDto) => ({ ...prev, ...params }));
    };

    const saveUser = (user: NubUserDTO) => {
        return saveUserMutation.mutateAsync(user);
    };

    const updateUserProfileMutation = useBaseMutation<NubUserDTO, NubUserDTO>(
        (user: NubUserDTO) => {
            if (!user.userId) {
                return createUser(user);
            }
            return updateUser(user.userId, user);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['users'] })
                    .then(() => {console.log('Profile updated successfully')});
            },
        }
    );

    const updatePassword = (userId: string, data: PasswordChangeRequest) => {
        return changePasswordMutation.mutateAsync({ userId, data });
    };

    const updateUserProfile = (user: NubUserDTO) => {
        return updateUserProfileMutation.mutateAsync(user);
    };
    return {
        users,
        isLoading,
        error,
        searchUsers,
        saveUser,
        updateUserProfile,
        changePassword: updatePassword,
        refetchUsers,
    };
};


export function useUserSearch() {
    return useBaseMutation<NubUserDTO[], string>(
        async (searchTerm: string) => {
            if (!searchTerm || searchTerm.length < 2) return [];
            const normalizedSearch = searchTerm.trim().toLowerCase();
            const allUsers = await getAllUsers();
            return (allUsers || [])
                .filter((user) => [user.fullName, user.email, user.userId]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(normalizedSearch)))
                .slice(0, 50);
        }
    );
}